import Component from '../component';
import Hex from '../hex';
import type LinearGradient from '../linear-gradient';
import type Stroke from '../stroke';
import {
	assertIsBoolean,
	assertIsChild,
	assertIsComponent,
	assertIsNaNOrFinitePositiveInteger,
	assertIsNonNegative,
	assertIsNonNegativeOrAuto,
	assertIsNotChild,
	assertIsValidAlignment,
	assertIsValidAutoLayout,
	assertIsValidCornerRadius,
	assertIsValidFill,
	assertIsValidPaddingHorizontalOrVertical,
	assertIsValidPaddingSide,
	assertIsValidStroke,
	assertIsValidVisible,
} from './container.assertions';

/**
 * The Container component is used when you want to create a container with child components.
 *
 * The Figma compatible Container component, has properties like padding, autoLayout, gap, cornerRadius etc.
 */
export default class Container extends Component {
	public static override observedAttributes: string[] = [
		...Component.observedAttributes,
		'cursor',
		'height',
		'max-height',
		'max-width',
		'min-height',
		'min-width',
		'opacity',
		'width',
		'alignment',
		'auto-layout',
		'clip-content',
		'fill',
		'padding-horizontal',
		'padding-vertical',
		'gap-vertical',
		'gap-horizontal',
		'corner-radius',
		'hide-scrollbars',
		'grid-column-count',
		'min-column-width',
	];
	/** The 9 possible placement areas, that you want position the child components. */
	public set alignment(value: 'bottom_center' | 'bottom_left' | 'bottom_right' | 'center' | 'left' | 'right' | 'top_center' | 'top_left' | 'top_right') {
		assertIsValidAlignment(value);
		this.#alignment = value;
		this.#alignmentChanged = true;
		this.invalidateProperties();
	}

	public get alignment(): 'bottom_center' | 'bottom_left' | 'bottom_right' | 'center' | 'left' | 'right' | 'top_center' | 'top_left' | 'top_right' {
		return this.#alignment;
	}

	/**
	 * Whether the vertical scrollbar is always shown, disabled while there is nothing to scroll, rather than appearing once the content outgrows the Container.
	 *
	 * For the room it takes: a scrollbar that appears takes its width off the content, which shifts everything beside it the moment a list grows one row too long. Shown from the start, the room is taken from the start, and a header laid out beside the list can be aligned to it once. The horizontal scrollbar is left as
	 * it is, shown and hidden as the content needs.
	 *
	 * Only with {@link Container.clipContent}, which is what gives the Container a scrollbar at all; without it this changes nothing. With {@link Container.hideScrollbars} it changes nothing either, since a hidden scrollbar has no width to take. Overlay scrollbars, as on macOS, take no room and draw no disabled bar, so
	 * there is nothing for this to do there — and nothing to shift.
	 *
	 * @param value Default false
	 */
	public set alwaysShowVerticalScrollbar(value: boolean) {
		assertIsBoolean(value);
		this.#alwaysShowVerticalScrollbar = value;
		this.#alwaysShowVerticalScrollbarChanged = true;
		this.invalidateProperties();
	}

	public get alwaysShowVerticalScrollbar(): boolean {
		return this.#alwaysShowVerticalScrollbar;
	}

	/** The auto layout that the Container should use, either 'grid', 'horizontal', 'vertical' or 'wrap', default is 'horizontal'. */
	public set autoLayout(value: 'grid' | 'horizontal' | 'vertical' | 'wrap') {
		assertIsValidAutoLayout(value);
		this.#autoLayout = value;
		this.#autoLayoutChanged = true;
		this.invalidateProperties();
	}

	public get autoLayout(): 'grid' | 'horizontal' | 'vertical' | 'wrap' {
		return this.#autoLayout;
	}

	/** The clipContent property, controls if the Container children will be clipped or not. */
	public set clipContent(value: boolean) {
		assertIsBoolean(value);
		this.#clipContent = value;
		this.#clipContentChanged = true;
		this.invalidateProperties();
	}

	public get clipContent(): boolean {
		return this.#clipContent;
	}

	/**
	 * The cornerRadius defines the curving in pixels of the Containers corners.
	 *
	 * If you provide a single number, all four corners will get the same pixel curving.
	 *
	 * If you provide a [number, number, number, number] tuple, the corner curving is set individually starting top left and going clockwise.
	 */
	public set cornerRadius(value: [number, number, number, number] | number) {
		assertIsValidCornerRadius(value);
		this.#cornerRadius = value;
		this.#cornerRadiusChanged = true;
		this.invalidateProperties();
	}

	public get cornerRadius(): [number, number, number, number] | number {
		return this.#cornerRadius;
	}

	/** The background color of the Container, either a Hex or LinearGradient instance or null. */
	public set fill(value: Hex | LinearGradient | null) {
		assertIsValidFill(value);
		this.#fill = value;
		this.#fillChanged = true;
		this.invalidateProperties();
	}

	public get fill(): Hex | LinearGradient | null {
		return this.#fill;
	}

	/** The horizontal gap between child components in a non negative number or 'auto' if you want even spacing. */
	public set gapHorizontal(value: 'auto' | number) {
		assertIsNonNegativeOrAuto(value);
		this.#gapHorizontal = value;
		this.#gapHorizontalChanged = true;
		this.invalidateProperties();
	}

	public get gapHorizontal(): 'auto' | number {
		return this.#gapHorizontal;
	}

	/** The vertical gap between child components in a non negative number or 'auto' if you want even spacing. */
	public set gapVertical(value: 'auto' | number) {
		assertIsNonNegativeOrAuto(value);
		this.#gapVertical = value;
		this.#gapVerticalChanged = true;
		this.invalidateProperties();
	}

	public get gapVertical(): 'auto' | number {
		return this.#gapVertical;
	}

	/** When auto-layout is 'grid', this value determines how many columns should be displayed in the grid. */
	public set gridColumnCount(value: number) {
		assertIsNaNOrFinitePositiveInteger(value);
		if (this.#gridColumnCount === value) {
			return;
		}
		if (Number.isNaN(this.#gridColumnCount) && Number.isNaN(value)) {
			return;
		}
		this.#gridColumnCount = value;
		this.#gridColumnCountChanged = true;
		this.invalidateProperties();
	}

	public get gridColumnCount(): number {
		return this.#gridColumnCount;
	}

	/** Hides the scrollbars when set to true, otherwise they will be shown if the content overflows. */
	public set hideScrollbars(value: boolean) {
		assertIsBoolean(value);
		this.#hideScrollbars = value;
		this.#hideScrollbarsChanged = true;
		this.invalidateProperties();
	}

	public get hideScrollbars(): boolean {
		return this.#hideScrollbars;
	}

	/** When auto-layout is 'grid', this value determines the minimum column width in pixels. */
	public set minColumnWidth(value: number) {
		assertIsNaNOrFinitePositiveInteger(value);
		if (this.#minColumnWidth === value) {
			return;
		}
		if (Number.isNaN(this.#minColumnWidth) && Number.isNaN(value)) {
			return;
		}
		this.#minColumnWidth = value;
		this.#minColumnWidthChanged = true;
		this.invalidateProperties();
	}

	public get minColumnWidth(): number {
		return this.#minColumnWidth;
	}

	/**
	 * The bottom padding of the Container as a non negative number.
	 *
	 * Setting it leaves the other three sides where they are, so it can be used to give one side its own value after paddingVertical has set both.
	 */
	public set paddingBottom(value: number) {
		assertIsValidPaddingSide(value);
		this.#paddingBottom = value;
		this.#paddingChanged = true;
		this.invalidateProperties();
	}

	public get paddingBottom(): number {
		return this.#paddingBottom;
	}

	/**
	 * The left and right padding of the Container as a non negative number.
	 *
	 * A number sets both sides, and a tuple sets them apart as [left, right]. Either way it writes the two sides, so it undoes a paddingLeft or paddingRight set before it.
	 *
	 * Reading it answers for what the two sides actually are: a number while they agree, and a [left, right] tuple once something has set them apart.
	 */
	public set paddingHorizontal(value: [number, number] | number) {
		assertIsValidPaddingHorizontalOrVertical(value);
		this.#paddingLeft = Array.isArray(value) ? value[0] : value;
		this.#paddingRight = Array.isArray(value) ? value[1] : value;
		this.#paddingChanged = true;
		this.invalidateProperties();
	}

	public get paddingHorizontal(): [number, number] | number {
		if (this.#paddingLeft === this.#paddingRight) {
			return this.#paddingLeft;
		}
		return [this.#paddingLeft, this.#paddingRight];
	}

	/**
	 * The left padding of the Container as a non negative number.
	 *
	 * Setting it leaves the other three sides where they are, so it can be used to give one side its own value after paddingHorizontal has set both.
	 */
	public set paddingLeft(value: number) {
		assertIsValidPaddingSide(value);
		this.#paddingLeft = value;
		this.#paddingChanged = true;
		this.invalidateProperties();
	}

	public get paddingLeft(): number {
		return this.#paddingLeft;
	}

	/**
	 * The right padding of the Container as a non negative number.
	 *
	 * Setting it leaves the other three sides where they are, so it can be used to give one side its own value after paddingHorizontal has set both.
	 */
	public set paddingRight(value: number) {
		assertIsValidPaddingSide(value);
		this.#paddingRight = value;
		this.#paddingChanged = true;
		this.invalidateProperties();
	}

	public get paddingRight(): number {
		return this.#paddingRight;
	}

	/**
	 * The top padding of the Container as a non negative number.
	 *
	 * Setting it leaves the other three sides where they are, so it can be used to give one side its own value after paddingVertical has set both.
	 */
	public set paddingTop(value: number) {
		assertIsValidPaddingSide(value);
		this.#paddingTop = value;
		this.#paddingChanged = true;
		this.invalidateProperties();
	}

	public get paddingTop(): number {
		return this.#paddingTop;
	}

	/**
	 * The top and bottom padding of the Container as a non negative number.
	 *
	 * A number sets both sides, and a tuple sets them apart as [top, bottom]. Either way it writes the two sides, so it undoes a paddingTop or paddingBottom set before it.
	 *
	 * Reading it answers for what the two sides actually are: a number while they agree, and a [top, bottom] tuple once something has set them apart.
	 */
	public set paddingVertical(value: [number, number] | number) {
		assertIsValidPaddingHorizontalOrVertical(value);
		this.#paddingBottom = Array.isArray(value) ? value[1] : value;
		this.#paddingTop = Array.isArray(value) ? value[0] : value;
		this.#paddingChanged = true;
		this.invalidateProperties();
	}

	public get paddingVertical(): [number, number] | number {
		if (this.#paddingTop === this.#paddingBottom) {
			return this.#paddingTop;
		}
		return [this.#paddingTop, this.#paddingBottom];
	}

	public set stroke(value: null | Stroke) {
		assertIsValidStroke(value);
		this.#stroke = value;
		this.#stroke_changed = true;
		this.invalidateProperties();
	}

	public get stroke(): null | Stroke {
		return this.#stroke;
	}

	override set visible(value: boolean) {
		assertIsValidVisible(value);
		this.#visible = value;
		this.#visibleChanged = true;
		this.invalidateProperties();
	}

	override get visible(): boolean {
		return this.#visible;
	}

	#alignment: 'bottom_center' | 'bottom_left' | 'bottom_right' | 'center' | 'left' | 'right' | 'top_center' | 'top_left' | 'top_right';

	#alignmentChanged: boolean;

	#alwaysShowVerticalScrollbar: boolean;

	#alwaysShowVerticalScrollbarChanged: boolean;

	#autoLayout: 'grid' | 'horizontal' | 'vertical' | 'wrap';

	#autoLayoutChanged: boolean;

	#clipContent: boolean;

	#clipContentChanged: boolean;

	#cornerRadius: [number, number, number, number] | number;

	#cornerRadiusChanged: boolean;

	#fill: Hex | LinearGradient | null;

	#fillChanged: boolean;

	#gapHorizontal: 'auto' | number;

	#gapHorizontalChanged: boolean;

	#gapVertical: 'auto' | number;

	#gapVerticalChanged: boolean;

	#gridColumnCount: number;

	#gridColumnCountChanged: boolean;

	#hideScrollbars: boolean;

	#hideScrollbarsChanged: boolean;

	#minColumnWidth: number;

	#minColumnWidthChanged: boolean;

	#paddingBottom: number;

	#paddingChanged: boolean;

	#paddingLeft: number;

	#paddingRight: number;
	#paddingTop: number;

	#stroke: null | Stroke;

	#stroke_changed: boolean;

	#visible: boolean;

	#visibleChanged: boolean;

	get #isAlignmentBottom(): boolean {
		return this.alignment === 'bottom_left' || this.alignment === 'bottom_center' || this.alignment === 'bottom_right';
	}

	get #isAlignmentMiddle(): boolean {
		return this.alignment === 'left' || this.alignment === 'center' || this.alignment === 'right';
	}

	get #isAlignmentTop(): boolean {
		return this.alignment === 'top_left' || this.alignment === 'top_center' || this.alignment === 'top_right';
	}

	public constructor() {
		super();
		this.#alignment = 'top_left';
		this.#alignmentChanged = false;
		this.#alwaysShowVerticalScrollbar = false;
		this.#alwaysShowVerticalScrollbarChanged = false;
		this.#autoLayout = 'horizontal';
		this.#autoLayoutChanged = false;
		this.#clipContent = false;
		this.#clipContentChanged = false;
		this.#cornerRadius = 0;
		this.#cornerRadiusChanged = false;
		this.#fill = null;
		this.#fillChanged = false;
		this.#gapHorizontal = 0;
		this.#gapHorizontalChanged = false;
		this.#gapVertical = 0;
		this.#gapVerticalChanged = false;
		this.#gridColumnCount = Number.NaN;
		this.#gridColumnCountChanged = false;
		this.#hideScrollbars = false;
		this.#hideScrollbarsChanged = false;
		this.#minColumnWidth = Number.NaN;
		this.#minColumnWidthChanged = false;
		this.#paddingBottom = 0;
		this.#paddingChanged = false;
		this.#paddingLeft = 0;
		this.#paddingRight = 0;
		this.#paddingTop = 0;
		this.#stroke = null;
		this.#stroke_changed = false;
		this.#visible = true;
		this.#visibleChanged = false;
	}

	public addComponent(value: Component): void {
		assertIsComponent(value);
		assertIsNotChild(value, this);
		this.appendChild(value);
	}

	/**
	 * The addComponents method, adds multiple Component instances as children of the Container.
	 *
	 * @param value - An array of Component instances
	 */
	public addComponents(value: Component[]): void {
		const fragment: DocumentFragment = document.createDocumentFragment();
		for (const component of value) {
			assertIsComponent(component);
			assertIsNotChild(component, this);
			fragment.appendChild(component);
		}
		this.appendChild(fragment);
	}

	/**
	 * Adds a Component as a child at that index, in front of the child holding the index now, so the children from there on move one along.
	 *
	 * An index at or past the end adds it last, the way addComponent() does. The children already there are not touched: a child the user is in keeps its focus, which it would not if the children were taken out and put back in order.
	 *
	 * @param index - The index the Component is to hold among the children.
	 * @param value - The Component to add.
	 */
	public addComponentAt(index: number, value: Component): void {
		assertIsNonNegative(index);
		assertIsComponent(value);
		assertIsNotChild(value, this);
		this.insertBefore(value, this.children.item(index));
	}

	/**
	 * Moves a child Component in front of another child, or to the end when the other is null, keeping its state: focus, text selection, animations and an open popover inside it all survive, because the Component is moved rather than removed and added again.
	 *
	 * Through moveBefore(), so the browser invokes connectedMoveCallback() in place of the disconnect and connect callbacks — see Component — and the Component is neither torn down nor set up on the way. Both have to be children of this Container; moving a child in front of itself does nothing.
	 *
	 * @param value - The child Component to move.
	 * @param before - The child Component to move it in front of, or null to move it last.
	 */
	public moveComponentBefore(value: Component, before: Component | null): void {
		assertIsComponent(value);
		assertIsChild(value, this);
		if (before !== null) {
			assertIsComponent(before);
			assertIsChild(before, this);
		}
		if (value === before) {
			return;
		}
		this.moveBefore(value, before);
	}

	/**
	 * The componentIndex() method, returns the index of the Component, so the first child Component will return 0 and so on. If the Component is not a child of the Container, Number.NaN will be returned.
	 *
	 * @param value - The Component to get the index of.
	 * @returns - Returns the index of the Component, Number.NaN if the Component is not a child of
	 * the IContainer
	 */
	public componentIndex(value: Component): number {
		assertIsComponent(value);
		const index: number = Array.from(this.children).indexOf(value);
		return index !== -1 ? index : Number.NaN;
	}

	/**
	 * The containsComponent() method returns a boolean value indicating whether a Component is a descendant of a given Container, that is the Container itself, one of its direct children (Components), one of the children's direct children, and so on.
	 *
	 * @param value - The Component to test with.
	 * @returns A boolean value that is true if component is contained in the Container, false if
	 * not.
	 */
	public containsComponent(value: Component): boolean {
		assertIsComponent(value);
		return this.contains(value);
	}

	/** The removeAllComponents(), removes all the child components. */
	public removeAllComponents(): void {
		while (this.firstChild) {
			this.removeChild(this.firstChild);
		}
	}

	/**
	 * The removeComponent() method removes the Component from it's parent the DOM.
	 *
	 * @param value - The component to remove as child node.
	 */
	public removeComponent(value: Component): void {
		assertIsComponent(value);
		value.remove();
	}

	protected override attributeChangedCallback(name: string, _: string, value: string) {
		super.attributeChangedCallback(name, _, value);
		if (name === 'alignment') {
			this.#alignmentAttributeChanged(value);
		} else if (name === 'auto-layout') {
			this.#autoLayoutAttributeChanged(value);
		} else if (name === 'clip-content') {
			this.#clipContentAttributeChanged(value);
		} else if (name === 'corner-radius') {
			this.#cornerRadiusAttributeChanged(value);
		} else if (name === 'fill') {
			this.#fillAttributeChanged(value);
		} else if (name === 'gap-vertical') {
			this.#gapVerticalAttributeChanged(value);
		} else if (name === 'gap-horizontal') {
			this.#gapHorizontalAttributeChanged(value);
		} else if (name === 'hide-scrollbars') {
			this.#hideScrollbarAttributeChanged(value);
		} else if (name === 'padding-horizontal') {
			this.#paddingHorizontalAttributeChanged(value);
		} else if (name === 'padding-vertical') {
			this.#paddingVerticalAttributeChanged(value);
		} else if (name === 'grid-column-count') {
			this.#gridColumnCountAttributeChanged(value);
		} else if (name === 'min-column-width') {
			this.#minColumnWidthAttributeChanged(value);
		}
	}

	protected override commitProperties(): void {
		super.commitProperties();
		if (this.#clipContentChanged || this.#alwaysShowVerticalScrollbarChanged) {
			this.#commitClipContent();
		}
		if (this.#cornerRadiusChanged) {
			this.#commitCornerRadius();
		}
		if (this.#visibleChanged || this.#autoLayoutChanged || this.#gapHorizontalChanged || this.#gapVerticalChanged || this.#alignmentChanged) {
			this.#commitLayout();
		}
		if (this.#fillChanged) {
			this.#commitFill();
		}
		if (this.#hideScrollbarsChanged) {
			this.#commitHideScrollbars();
		}
		if (this.#paddingChanged) {
			this.#commitPaddingProperties();
		}
		if (this.#stroke_changed) {
			this.#commitStroke();
		}
		if (this.#minColumnWidthChanged || this.#gridColumnCountChanged) {
			this.#commitGridProperties();
		}
	}

	protected override initialize(): void {
		super.initialize();
		this.#setDefaultStyles();
	}

	#alignmentAttributeChanged(value: string): void {
		assertIsValidAlignment(value);
		this.alignment = value;
	}

	/** Aligns the children the way the layout aligns them. A grid is not aligned at all. */
	#applyAlignment(): void {
		if (this.autoLayout === 'vertical') {
			this.#autoLayoutVerticalAlignment();
			return;
		}
		if (this.autoLayout === 'horizontal') {
			this.#autoLayoutHorizontalAlignment();
			return;
		}
		if (this.autoLayout === 'wrap') {
			this.#autoLayoutWrapAlignment();
		}
	}

	/** Shows the Container as the layout it has, or hides it. */
	#applyDisplay(): void {
		if (this.#visible) {
			this.#setDefaultAutoLayoutStyles();
			if (this.autoLayout === 'horizontal') {
				this.style.flexDirection = 'row';
			} else if (this.autoLayout === 'vertical') {
				this.style.flexDirection = 'column';
			} else if (this.autoLayout === 'wrap') {
				this.style.flexDirection = 'row';
				this.style.flexWrap = 'wrap';
			} else {
				this.style.display = 'inline-grid';
				this.style.gridAutoRows = 'min-content';
			}
			if (this.autoLayout !== 'grid') {
				this.#updateChildrenParentAutoLayout();
			}
		} else {
			this.style.display = 'none';
		}
	}

	#applyGapHorizontal(): void {
		if (this.#autoLayout === 'horizontal' || this.#autoLayout === 'wrap') {
			if (this.#gapHorizontal === 'auto') {
				this.style.justifyContent = 'space-between';
				this.style.columnGap = '';
			} else {
				this.style.columnGap = `${this.#gapHorizontal}px`;
			}
		} else if (this.#autoLayout === 'vertical') {
			if (this.#gapHorizontal !== 'auto') {
				this.style.columnGap = '';
			}
		} else {
			// grid: the columns are 1fr and take every pixel of the width between them, so 'auto' has
			// nothing left over to spread and can only mean no gap at all
			this.style.columnGap = this.#gapHorizontal === 'auto' ? '' : `${this.#gapHorizontal}px`;
		}
	}

	#applyGapVertical(): void {
		if (this.#autoLayout === 'vertical') {
			if (this.#gapVertical === 'auto') {
				this.style.justifyContent = 'space-between';
				this.style.rowGap = '';
			} else {
				this.style.rowGap = `${this.#gapVertical}px`;
			}
		} else if (this.#autoLayout === 'horizontal') {
			if (this.#gapVertical !== 'auto') {
				this.style.rowGap = '';
			}
		} else if (this.#autoLayout === 'wrap') {
			if (this.#gapVertical === 'auto') {
				this.style.alignContent = 'space-between';
				this.style.rowGap = '';
			} else {
				this.style.rowGap = `${this.#gapVertical}px`;
			}
		} else {
			// grid: the rows are only as tall as what they hold, so a taller grid has height left over,
			// and 'auto' spreads the rows over it the way wrap does. Set back with a number, since
			// alignment does not touch a grid and nothing else would take a spread back
			if (this.#gapVertical === 'auto') {
				this.style.alignContent = 'space-between';
				this.style.rowGap = '';
			} else {
				this.style.alignContent = '';
				this.style.rowGap = `${this.#gapVertical}px`;
			}
		}
	}

	#autoLayoutAttributeChanged(value: string): void {
		assertIsValidAutoLayout(value);
		this.autoLayout = value;
	}

	#autoLayoutHorizontalAlignment(): void {
		if (this.#isAlignmentTop) {
			this.#autoLayoutHorizontalAlignmentTop();
		} else if (this.#isAlignmentMiddle) {
			this.#autoLayoutHorizontalAlignmentMiddle();
		} else if (this.#isAlignmentBottom) {
			this.#autoLayoutHorizontalAlignmentBottom();
		}
		this.#updateChildrenParentAutoLayout();
	}

	#autoLayoutHorizontalAlignmentBottom(): void {
		if (this.alignment === 'bottom_left') {
			this.style.justifyContent = this.#gapHorizontal === 'auto' ? 'space-between' : 'flex-start';
			this.style.alignItems = 'flex-end';
		} else if (this.alignment === 'bottom_center') {
			this.style.justifyContent = this.#gapHorizontal === 'auto' ? 'space-between' : 'center';
			this.style.alignItems = 'flex-end';
		} else if (this.alignment === 'bottom_right') {
			this.style.justifyContent = this.#gapHorizontal === 'auto' ? 'space-between' : 'flex-end';
			this.style.alignItems = 'flex-end';
		}
	}

	#autoLayoutHorizontalAlignmentMiddle(): void {
		if (this.alignment === 'left') {
			this.style.justifyContent = this.#gapHorizontal === 'auto' ? 'space-between' : 'flex-start';
			this.style.alignItems = 'center';
		} else if (this.alignment === 'center') {
			this.style.justifyContent = this.#gapHorizontal === 'auto' ? 'space-between' : 'center';
			this.style.alignItems = 'center';
		} else if (this.alignment === 'right') {
			this.style.justifyContent = this.#gapHorizontal === 'auto' ? 'space-between' : 'flex-end';
			this.style.alignItems = 'center';
		}
	}

	#autoLayoutHorizontalAlignmentTop(): void {
		if (this.alignment === 'top_left') {
			this.style.justifyContent = this.#gapHorizontal === 'auto' ? 'space-between' : 'flex-start';
			this.style.alignItems = 'flex-start';
		} else if (this.alignment === 'top_center') {
			this.style.justifyContent = this.#gapHorizontal === 'auto' ? 'space-between' : 'center';
			this.style.alignItems = 'flex-start';
		} else if (this.alignment === 'top_right') {
			this.style.justifyContent = this.#gapHorizontal === 'auto' ? 'space-between' : 'flex-end';
			this.style.alignItems = 'flex-start';
		}
	}

	#autoLayoutVerticalAlignment(): void {
		if (this.#isAlignmentTop) {
			this.#autoLayoutVerticalAlignmentTop();
		} else if (this.#isAlignmentMiddle) {
			this.#autoLayoutVerticalAlignmentMiddle();
		} else if (this.#isAlignmentBottom) {
			this.#autoLayoutVerticalAlignmentBottom();
		}
		this.#updateChildrenParentAutoLayout();
	}

	#autoLayoutVerticalAlignmentBottom(): void {
		if (this.alignment === 'bottom_left') {
			this.style.justifyContent = this.#gapVertical === 'auto' ? 'space-between' : 'flex-end';
			this.style.alignItems = 'flex-start';
		} else if (this.alignment === 'bottom_center') {
			this.style.justifyContent = this.#gapVertical === 'auto' ? 'space-between' : 'flex-end';
			this.style.alignItems = 'center';
		} else if (this.alignment === 'bottom_right') {
			this.style.justifyContent = this.#gapVertical === 'auto' ? 'space-between' : 'flex-end';
			this.style.alignItems = 'flex-end';
		}
	}

	#autoLayoutVerticalAlignmentMiddle(): void {
		if (this.alignment === 'left') {
			this.style.justifyContent = this.#gapVertical === 'auto' ? 'space-between' : 'center';
			this.style.alignItems = 'flex-start';
		} else if (this.alignment === 'center') {
			this.style.justifyContent = this.#gapVertical === 'auto' ? 'space-between' : 'center';
			this.style.alignItems = 'center';
		} else if (this.alignment === 'right') {
			this.style.justifyContent = this.#gapVertical === 'auto' ? 'space-between' : 'center';
			this.style.alignItems = 'flex-end';
		}
	}

	#autoLayoutVerticalAlignmentTop(): void {
		if (this.alignment === 'top_left') {
			this.style.justifyContent = this.#gapVertical === 'auto' ? 'space-between' : 'flex-start';
			this.style.alignItems = 'flex-start';
		} else if (this.alignment === 'top_center') {
			this.style.justifyContent = this.#gapVertical === 'auto' ? 'space-between' : 'flex-start';
			this.style.alignItems = 'center';
		} else if (this.alignment === 'top_right') {
			this.style.justifyContent = this.#gapVertical === 'auto' ? 'space-between' : 'flex-start';
			this.style.alignItems = 'flex-end';
		}
	}

	#autoLayoutWrapAlignment(): void {
		if (this.#isAlignmentTop) {
			this.#autoLayoutWrapAlignmentTop();
		} else if (this.#isAlignmentMiddle) {
			this.#autoLayoutWrapAlignmentMiddle();
		} else if (this.#isAlignmentBottom) {
			this.#autoLayoutWrapAlignmentBottom();
		}
		this.#updateChildrenParentAutoLayout();
	}

	#autoLayoutWrapAlignmentBottom(): void {
		if (this.alignment === 'bottom_left') {
			this.#autoLayoutWrapAlignmentBottomLeft();
		} else if (this.alignment === 'bottom_center') {
			this.#autoLayoutWrapAlignmentBottomCenter();
		} else {
			this.#autoLayoutWrapAlignmentBottomRight();
		}
		this.style.alignItems = '';
	}

	#autoLayoutWrapAlignmentBottomCenter(): void {
		this.style.justifyContent = this.#gapHorizontal === 'auto' ? 'space-between' : 'center';
		this.style.alignContent = this.#gapVertical === 'auto' ? 'space-between' : 'flex-end';
	}

	#autoLayoutWrapAlignmentBottomLeft(): void {
		this.style.justifyContent = this.#gapHorizontal === 'auto' ? 'space-between' : 'flex-start';
		this.style.alignContent = this.#gapVertical === 'auto' ? 'space-between' : 'flex-end';
	}

	#autoLayoutWrapAlignmentBottomRight(): void {
		this.style.justifyContent = this.#gapHorizontal === 'auto' ? 'space-between' : 'flex-end';
		this.style.alignContent = this.#gapVertical === 'auto' ? 'space-between' : 'flex-end';
	}

	#autoLayoutWrapAlignmentMiddle(): void {
		if (this.alignment === 'left') {
			this.#autoLayoutWrapAlignmentMiddleLeft();
		} else if (this.alignment === 'center') {
			this.#autoLayoutWrapAlignmentMiddleCenter();
		} else if (this.alignment === 'right') {
			this.#autoLayoutWrapAlignmentMiddleRight();
		}
	}

	#autoLayoutWrapAlignmentMiddleCenter(): void {
		this.style.justifyContent = this.#gapHorizontal === 'auto' ? 'space-between' : 'center';
		this.style.alignItems = '';
		this.style.alignContent = this.#gapVertical === 'auto' ? 'space-between' : 'center';
	}

	#autoLayoutWrapAlignmentMiddleLeft(): void {
		this.style.justifyContent = this.#gapHorizontal === 'auto' ? 'space-between' : 'flex-start';
		this.style.alignItems = '';
		this.style.alignContent = this.#gapVertical === 'auto' ? 'space-between' : 'center';
	}

	#autoLayoutWrapAlignmentMiddleRight(): void {
		this.style.justifyContent = this.#gapHorizontal === 'auto' ? 'space-between' : 'flex-end';
		this.style.alignItems = '';
		this.style.alignContent = this.#gapVertical === 'auto' ? 'space-between' : 'center';
	}

	#autoLayoutWrapAlignmentTop(): void {
		if (this.alignment === 'top_left') {
			this.#autoLayoutWrapAlignmentTopLeft();
		} else if (this.alignment === 'top_center') {
			this.#autoLayoutWrapAlignmentTopCenter();
		} else if (this.alignment === 'top_right') {
			this.#autoLayoutWrapAlignmentTopRight();
		}
	}

	#autoLayoutWrapAlignmentTopCenter(): void {
		this.style.justifyContent = this.#gapHorizontal === 'auto' ? 'space-between' : 'center';
		this.style.alignItems = '';
		this.style.alignContent = this.#gapVertical === 'auto' ? 'space-between' : 'flex-start';
	}

	#autoLayoutWrapAlignmentTopLeft(): void {
		this.style.justifyContent = this.#gapHorizontal === 'auto' ? 'space-between' : 'flex-start';
		this.style.alignItems = '';
		this.style.alignContent = this.#gapVertical === 'auto' ? 'space-between' : 'flex-start';
	}

	#autoLayoutWrapAlignmentTopRight(): void {
		this.style.justifyContent = this.#gapHorizontal === 'auto' ? 'space-between' : 'flex-end';
		this.style.alignItems = '';
		this.style.alignContent = this.#gapVertical === 'auto' ? 'space-between' : 'flex-start';
		this.#updateChildrenParentAutoLayout();
	}

	#clipContentAttributeChanged(value: string) {
		if (value === 'true') {
			this.clipContent = true;
		} else {
			this.clipContent = false;
		}
	}

	/**
	 * Writes whether the content is clipped, and, when it is, whether the vertical scrollbar waits for the content to outgrow the Container or is there from the start.
	 *
	 * The two axes are written apart, because only the vertical one is ever forced — see {@link Container.alwaysShowVerticalScrollbar}. Without clipping there is no scrollbar to force, so the shorthand is cleared and both axes go with it.
	 */
	#commitClipContent(): void {
		this.#clipContentChanged = false;
		this.#alwaysShowVerticalScrollbarChanged = false;
		if (this.clipContent) {
			this.style.overflowX = 'auto';
			this.style.overflowY = this.#alwaysShowVerticalScrollbar ? 'scroll' : 'auto';
		} else {
			this.style.overflow = '';
		}
	}

	#commitCornerRadius(): void {
		this.#cornerRadiusChanged = false;
		if (Array.isArray(this.cornerRadius)) {
			this.style.borderRadius = `${this.cornerRadius[0]}px ${this.cornerRadius[1]}px ${this.cornerRadius[2]}px ${this.cornerRadius[3]}px`;
		} else {
			this.style.borderRadius = `${this.cornerRadius}px`;
		}
	}

	#commitFill(): void {
		this.#fillChanged = false;
		if (this.fill === null) {
			this.style.background = '';
			return;
		}
		this.style.background = this.fill.toStyleString();
	}

	#commitGridProperties(): void {
		this.#gridColumnCountChanged = false;
		this.#minColumnWidthChanged = false;
		// if minColumnWidth and gridColumnCount are NaN, we reset the gridTemplateColumns value
		if (Number.isNaN(this.#minColumnWidth) && Number.isNaN(this.#gridColumnCount)) {
			this.style.gridTemplateColumns = '';
		}
		// if only gridColumnCount is set, we set gridTemplateColumns without minMax()
		else if (Number.isNaN(this.#minColumnWidth) && !Number.isNaN(this.#gridColumnCount)) {
			this.style.gridTemplateColumns = `repeat(${this.#gridColumnCount}, 1fr)`;
		}
		// if minColumnWidth is set, it overrides the gridColumnCount and uses minMax() pixel value
		else if (!Number.isNaN(this.#minColumnWidth)) {
			this.style.gridTemplateColumns = `repeat(auto-fill, minmax(${this.#minColumnWidth}px, 1fr))`;
		}
	}

	#commitHideScrollbars(): void {
		this.#hideScrollbarsChanged = false;
		if (this.hideScrollbars) {
			this.style.scrollbarWidth = 'none';
		} else {
			this.style.scrollbarWidth = '';
		}
	}

	/**
	 * Writes how the Container lays out its children: whether it is shown, which layout, the gaps between the children and how they are aligned.
	 *
	 * One commit for five properties, because they are not independent. The display a Container gets depends on whether it is visible and on its layout, and the gaps and the alignment are written differently for every layout — a gap one layout set would otherwise stay when another layout takes over, and one the new
	 * layout needs would never be written. Committed apart, each would have to know which of the others had changed in the same pass, and the order of commitProperties would decide it.
	 *
	 * So every flag is cleared here and everything is written, in the one order that works: the display first, then the gaps, then the alignment, which reads the gaps to decide on space-between. The same trade as #commitPaddingProperties: a few writes that cost nothing, against flags kept in step by hand.
	 */
	#commitLayout(): void {
		this.#alignmentChanged = false;
		this.#autoLayoutChanged = false;
		this.#gapHorizontalChanged = false;
		this.#gapVerticalChanged = false;
		this.#visibleChanged = false;
		this.#applyDisplay();
		this.#applyGapHorizontal();
		this.#applyGapVertical();
		this.#applyAlignment();
	}

	/**
	 * Writes all four sides, since they are four style properties and one flag between them.
	 *
	 * Tracking a dirty bit per side would be four flags to keep in step for four writes that cost nothing, where whichever side changed is the only thing they could disagree about.
	 */
	#commitPaddingProperties(): void {
		this.#paddingChanged = false;
		this.style.paddingBottom = `${this.#paddingBottom}px`;
		this.style.paddingLeft = `${this.#paddingLeft}px`;
		this.style.paddingRight = `${this.#paddingRight}px`;
		this.style.paddingTop = `${this.#paddingTop}px`;
	}

	#commitStroke(): void {
		this.#stroke_changed = false;
		if (this.stroke) {
			this.style.outline = this.stroke.toStyleString();
			this.style.outlineOffset = this.stroke.toStyleOffsetString();
		} else {
			this.style.outline = 'none';
			this.style.outlineOffset = '';
		}
	}

	#cornerRadiusAttributeChanged(value: string): void {
		this.cornerRadius = Number(value);
	}

	#fillAttributeChanged(value: string): void {
		if (value === '') {
			this.fill = null;
		} else {
			this.fill = new Hex(value);
		}
	}

	#gapHorizontalAttributeChanged(value: string): void {
		if (value === 'auto') {
			this.gapHorizontal = value;
		} else {
			this.gapHorizontal = Number(value);
		}
	}

	#gapVerticalAttributeChanged(value: string): void {
		if (value === 'auto') {
			this.gapVertical = value;
		} else {
			this.gapVertical = Number(value);
		}
	}

	#gridColumnCountAttributeChanged(value: string): void {
		this.gridColumnCount = Number(value);
	}

	#hideScrollbarAttributeChanged(value: string): void {
		if (value === 'true') {
			this.hideScrollbars = true;
		} else {
			this.hideScrollbars = false;
		}
	}

	#minColumnWidthAttributeChanged(value: string): void {
		this.minColumnWidth = Number(value);
	}

	#paddingHorizontalAttributeChanged(value: string): void {
		this.paddingHorizontal = Number(value);
	}

	#paddingVerticalAttributeChanged(value: string): void {
		this.paddingVertical = Number(value);
	}

	#setDefaultAutoLayoutStyles(): void {
		this.style.gridAutoRows = '';
		this.style.flexWrap = '';
		this.style.flexDirection = '';
		this.style.display = 'inline-flex';
	}

	#setDefaultStyles(): void {
		this.style.alignItems = 'flex-start';
		this.style.columnGap = '';
		this.style.display = 'inline-flex';
		this.style.flexDirection = 'row';
		this.style.justifyContent = 'flex-start';
		this.style.outlineOffset = '';
		this.style.overflow = '';
		this.style.padding = '';
		this.style.rowGap = '';
	}

	#updateChildrenParentAutoLayout(): void {
		for (const child of this.children) {
			if (child instanceof Component) {
				child.parentAutoLayout = this.autoLayout;
			}
		}
	}
}
customElements.define('ac-container', Container);
